<template>
  <div id="app" :class="[whiteLabel ? 'theme-new-website' : 'theme-default']">
    <Header :whiteLabel="whiteLabel"/>

    <transition tag="main" name="fade" mode="out-in" v-if="loaded">
      <section v-if="!loggedIn">
        <router-view :key="_uid"/>
      </section>

      <section v-else class="main" :class="{animate: loggedIn}">
        <Sidebar
          :navActive="navActive"
          @closeNav="closeNav"
          :whiteLabel="whiteLabel"/>

        <section class="content">
          <TopNav @toggleNav="toggleNav" />
          <section
            @click="closeNav"
            class="main-content"
            :class="{ 'in-builder-mode': inBuilderMode }">
            <transition name="fade" mode="out-in">
              <router-view/>
            </transition>
          </section>
        </section>
      </section>
    </transition>
  </div>
</template>

<script>
import {mapState, mapGetters} from 'vuex'
import * as types from '@/store/mutation_types'

import Vue from 'vue'
import VueAnalytics from 'vue-analytics'
import Header from '@/components/Header'
import TopNav from '@/components/TopNav'
import Sidebar from '@/components/Sidebar'

export default {
  name: 'app',

  components: {
    Header,
    Sidebar,
    TopNav
  },

  data() {
    return {
      navActive: false,
      loaded: false,
      whiteLabel: ''
    }
  },

  computed: {
    ...mapState(['session', 'currentUser']),
    ...mapGetters(['loggedIn']),

    userCompany() {
      return this.currentUser.company || {}
    },

    inBuilderMode() {
      return this.$route.meta.hideSidebar
    }
  },

  methods: {
    setTheme() {
      const isWhitelabel = 'new-website'
      this.whiteLabel = window.location.host.includes(isWhitelabel) || window.location.host.includes('localhost')
      this.$store.commit(types.WHITE_LABEL, this.whiteLabel)
      this.loaded = true
    },

    initGoogleAnalytics() {
      const prod = process.env.NODE_ENV === 'production'

      Vue.use(VueAnalytics, {
        debug: {
          enabled: !prod,
          sendHitTask: prod
        },

        router: this.$router,
        id: process.env.VUE_APP_ANALYTICS_ID

      })
    },

    toggleNav() {
      this.navActive = !this.navActive
    },

    closeNav() {
      this.navActive = false
    }
  },

  mounted() {
    this.setTheme()
    this.initGoogleAnalytics()
  }
}
</script>

<style lang="sass">
@import 'stylesheets/base/variables'
@import 'stylesheets/base/typography'
@import 'stylesheets/base/theme'
@import 'stylesheets/helpers/mixins'
@import 'stylesheets/helpers/extends'

html, *
  box-sizing: border-box
  scroll-behavior: smooth

  &::selection, &::-moz-selection
    @include themify($themes)
      background: themed('color-highlight')

body
  margin: 0
  font-size: 11px
  font-family: arial
  letter-spacing: 1px

  @include themify($themes)
    color: themed('color-body')

a
  text-decoration: none

#app
  font-family: 'Avenir', Helvetica, Arial, sans-serif
  -webkit-font-smoothing: antialiased
  -moz-osx-font-smoothing: grayscale
  min-height: 100vh
  display: flex
  flex-direction: column
  flex-wrap: nowrap

.fade-enter-active, .fade-leave-active
  transition: opacity .2s ease-in-out

.fade-enter, .fade-leave-to
  opacity: 0

.slide-up-enter-active, .slide-up-leave-active
  transition: all .3s ease-in-out

.slide-up-enter, .slide-up-leave-to
  opacity: 0
  transform: translateY(5px)

.main
  display: flex
  flex: 1 1 auto

.content
  background: $color-background
  width: 100%
  display: flex
  flex-direction: column
  overflow: hidden

.main-content
  display: flex
  flex-direction: column
  justify-content: space-between
  overflow: scroll
  height: $content-height
  opacity: 0
  animation: fadeIn 1s 1.5s
  animation-fill-mode: forwards

  &.in-builder-mode
    height: 100%

.page-columns
  background: #fff
  min-height: calc(100% - 95px)

  @include media($media-gt-large)
    display: flex

  .col
    margin-bottom: $gap-large

    @include media($media-gt-medium)
      margin-bottom: 0
      flex-basis: 50%

      &:first-child
        padding-right: $gap-medium

  .col
    &.main
      background: $color-column
      position: relative

      @include media($media-gt-medium)
        flex-basis: 70%

    &.side
      @include media($media-gt-medium)
        flex-basis: 30%
        padding: 0 $gap-small

.page-content
  width: 100%
  padding: $gap-small

  @include media($media-gt-medium)
    padding: $gap-medium 0 $gap-medium $gap-medium

  .pagination
    margin-top: $gap-small

.user-action
  display: flex
  align-items: center

.two-columns
  @extend %two-columns

.option-heading
  padding-bottom: $gap-small
  margin-bottom: $gap-medium
  border-bottom: 1px solid $color-border
  width: 100%

.heading-button
  position: absolute
  top: calc(50% - #{$gap-small})
  transform: translateY(-50%)
  right: 0

select
  -webkit-appearance: none
  appearance: none
  border: none
  line-height: 1
  outline: 0
  padding: 8px 28px 8px 8px
  border-radius: 3px
  border: 1px solid $color-select-fg
  background-color: $color-select-light
  background-image: linear-gradient($color-border, $color-border), linear-gradient(-135deg, transparent 50%, $color-select-fg 50%), linear-gradient(-225deg, transparent 50%, $color-select-fg 50%), linear-gradient($color-select-fg 42%, $color-select-bg 42%)
  background-repeat: no-repeat, no-repeat, no-repeat, no-repeat
  background-size: 1px 100%, 20px 22px, 20px 22px, 20px 100%
  background-position: right 20px center, right bottom, right bottom, right bottom
  cursor: pointer
  color: $color-select-text

  &.is-dark
    background-color: $color-select

  &:active
    background-image: linear-gradient($color-select-bg, $color-select-bg), linear-gradient(-135deg, transparent 50%, $color-select-bg 50%), linear-gradient(-225deg, transparent 50%, $color-select-bg 50%), linear-gradient($color-select-bg 42%, $color-select-fg 42%)
    color: $color-select-fg
    background-color: $color-select-bg

hr
  background: $color-border
  border: none
  height: 1px
  width: 100%
  margin: $gap-large 0
</style>
